import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@iconify/react";

interface StatisticsCardProps {
  onlineUsers?: number;
  loadingOnline?: boolean;
  usersHistory?: Array<{ date: string; count: number }>;
  totalUsers?: number;
  days?: number;
  allHistoryData?: Array<{ date: string; count: number }>;
  totalDistance?: number;
  loadingDistance?: boolean;
}

const mockStats = [
  {
    title: "Total Revenue",
    subtitle: "$1,250.00",
    cardIcon: "solar:chart-square-line-duotone",
    badgeColor: "bg-teal-400/10",
    statusValue: "+12.5%",
    statusIcon: "solar:course-up-line-duotone",
  },
  {
    title: "New Users",
    subtitle: "1,234",
    cardIcon: "solar:users-group-rounded-line-duotone",
    badgeColor: "bg-orange-400/10",
    statusValue: "-20%",
    statusIcon: "solar:course-down-line-duotone",
  },
];

export function StatisticsCard({
  onlineUsers,
  loadingOnline,
  usersHistory = [],
  totalUsers = 0,
  days = 365,
  allHistoryData = [],
  totalDistance = 0,
  loadingDistance = false,
}: StatisticsCardProps) {
  // Calculate growth rate based on the selected time interval
  const calculateGrowthRate = (): {
    value: number;
    direction: "up" | "down";
  } => {
    if (!usersHistory || usersHistory.length < 1)
      return { value: 0, direction: "up" };

    const sortedData = [...usersHistory].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    // Total new users registered in the selected period
    const totalNewUsersInPeriod = sortedData.reduce(
      (sum, d) => sum + d.count,
      0,
    );

    if (totalNewUsersInPeriod === 0) return { value: 0, direction: "up" };

    const currentTotal = allHistoryData.reduce((sum, d) => sum + d.count, 0);
    const startingUserbase = Math.max(0, currentTotal - totalNewUsersInPeriod);
    const growthRate = (totalNewUsersInPeriod / startingUserbase) * 100;

    return {
      value: growthRate,
      direction: growthRate >= 0 ? "up" : "down",
    };
  };

  const growthRate = calculateGrowthRate();
  const totalNewUsersInPeriod = usersHistory.reduce(
    (sum, d) => sum + d.count,
    0,
  );

  const totalDistanceKm = totalDistance / 1000;

  const dynamicStats = [
    {
      title: "Total Distance Traveled",
      subtitle: loadingDistance ? (
        <Skeleton className="h-8 w-24" />
      ) : (
        `${totalDistanceKm.toLocaleString(undefined, { maximumFractionDigits: 1 })} Km`
      ),
      cardIcon: "solar:streets-map-point-line-duotone",
      badgeColor: "bg-teal-400/10",
      statusValue: "Live",
      statusIcon: "solar:course-up-line-duotone",
    },
    {
      title: "New Users",
      subtitle: totalNewUsersInPeriod.toLocaleString(),
      cardIcon: "solar:users-group-rounded-line-duotone",
      badgeColor: "bg-teal-400/10",
      statusValue: `+${totalNewUsersInPeriod.toLocaleString()}`,
      statusIcon: "solar:course-up-line-duotone",
    },
    {
      title: "Growth Rate",
      subtitle: `${growthRate.value.toFixed(2)}%`,
      cardIcon: "solar:graph-up-line-duotone",
      badgeColor: "bg-teal-400/10",
      statusValue: `+${growthRate.value.toFixed(2)}%`,
      statusIcon: "solar:course-up-line-duotone",
    },
  ];
  return (
    <div className="px-4 lg:px-6 w-full">
      <Card className="p-0">
        <CardContent className="flex w-full flex-col items-stretch px-0 md:flex-row md:flex-nowrap">
          {/* Live online users */}
          <div className="flex-1 border-b border-border md:border-b-0 md:border-e">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-start">
                  <h5 className="text-base font-medium text-muted-foreground">
                    Online now
                  </h5>
                  <div className="p-3 rounded-full outline outline-border text-primary">
                    <span className="relative flex size-4 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex size-2 rounded-full bg-green-500" />
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <h5 className="text-2xl font-semibold">
                    {loadingOnline ? (
                      <Skeleton className="h-8 w-20" />
                    ) : (
                      (onlineUsers ?? 0).toLocaleString()
                    )}
                  </h5>
                  <p className="text-xs text-muted-foreground">
                    Members in the geospatial index
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mock stats */}
          {dynamicStats.map((item, index) => (
            <div
              key={index}
              className="flex-1 border-b border-border last:border-b-0 md:border-b-0 md:border-e md:last:border-e-0"
            >
              <div className="p-4 sm:p-6">
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between items-start">
                    <h5 className="text-base font-medium text-muted-foreground">
                      {item.title}
                    </h5>
                    <div className="p-3 rounded-full outline outline-border text-primary">
                      {typeof item.cardIcon === "string" ? (
                        <Icon icon={item.cardIcon} width={16} height={16} />
                      ) : (
                        item.cardIcon
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <h5 className="text-2xl font-semibold">{item.subtitle}</h5>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-muted-foreground">
                        Last {days} days
                      </p>
                      <Badge
                        className={`${item.badgeColor} text-muted-foreground`}
                      >
                        <div className="flex items-center gap-1">
                          {item.statusValue}
                          <Icon icon={item.statusIcon} width={14} height={14} />
                        </div>
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
